import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumb from "../../components/ui/Breadcrumb";
import MobileNavigation from "../../components/ui/MobileNavigation";
import Icon from "../../components/AppIcon";
import Button from "../../components/ui/Button";
import { useSelector } from "react-redux";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const CustomerPage = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  // Redux state → Assuming customers are stored in redux
  const { customer } = useSelector((state) => state.sale);
  let [customers, setCustomers] = useState(customer);

  // 🔹 Filters
  const [search, setSearch] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      setUser(JSON.parse(userData));
    } else {
      navigate("/whats-app-authentication");
    }
  }, [navigate]);

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleMobileNavToggle = () => {
    setMobileNavOpen(!mobileNavOpen);
  };

  // 🔹 Filtered Customer Data
  const filteredCustomers = customers?.filter((cust) => {
    const matchSearch =
      cust.name.toLowerCase().includes(search.toLowerCase()) ||
      cust.mobile.includes(search);

    const matchEmployee =
      !selectedEmployee || cust?._id === selectedEmployee;

    return matchSearch && matchEmployee;
  });

  // 🔹 Export PDF
  const handleExportPDF = () => {
    if (!filteredCustomers || filteredCustomers.length === 0) {
      alert("No customers to export");
      return;
    }

    const doc = new jsPDF();

    // Title
    doc.setFontSize(18);
    doc.text("Customer Report", 14, 20);

    doc.setFontSize(11);
    doc.setTextColor(100);
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 28);

    // Table data
    const headers = [["#", "Name", "Mobile", "Email", "Total Sales", "Last Visited"]];
    const rows = filteredCustomers.map((cust, index) => [
      index + 1,
      cust.name,
      cust.mobile,
      cust.email || "-",
      cust?.totalSpent || 0,
      cust.lastVisited
        ? new Date(cust.lastVisited).toLocaleDateString()
        : "Never",
    ]);

    autoTable(doc, {
      head: headers,
      body: rows,
      startY: 35,
      styles: { fontSize: 10 },
      headStyles: { fillColor: [41, 128, 185] },
      alternateRowStyles: { fillColor: [245, 245, 245] },
    });

    doc.save(`customer_report_${new Date().toISOString().slice(0, 10)}.pdf`);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <Icon
            name="Loader2"
            size={32}
            className="animate-spin mx-auto mb-4 text-primary"
          />
          <p className="text-muted-foreground">Loading customer page...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header
        user={user}
        onSidebarToggle={handleMobileNavToggle}
        sidebarCollapsed={sidebarCollapsed}
      />

      <Sidebar
        user={user}
        collapsed={sidebarCollapsed}
        onToggle={handleSidebarToggle}
      />

      <MobileNavigation
        user={user}
        isOpen={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
      />

      <main
        className={`pt-16 transition-all duration-200 ${
          sidebarCollapsed ? "lg:pl-16" : "lg:pl-60"
        }`}
      >
        <div className="p-6">
          <Breadcrumb user={user} />

          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-8">
            <div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                Customer Management
              </h1>
              <p className="text-muted-foreground">
                View, filter, and manage your customers.
              </p>
            </div>
            <div className="flex items-center space-x-3 mt-4 sm:mt-0">
              <Button
                variant="outline"
                iconName="Download"
                iconPosition="left"
                iconSize={16}
                onClick={handleExportPDF} // 🔹 Export handler
              >
                Export
              </Button>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-card border border-border rounded-lg p-6 mb-8">
            <div className="flex flex-wrap gap-3 items-center">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search by name or mobile..."
                className="border border-border rounded px-3 py-2 text-sm flex-1"
              />
              <select
                value={selectedEmployee}
                onChange={(e) => setSelectedEmployee(e.target.value)}
                className="border border-border rounded px-3 py-2 text-sm"
              >
                <option value="">All Employees</option>
                {customers?.map((emp) => (
                  <option key={emp?._id} value={emp?._id}>
                    {emp?.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Customer Table */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm text-left">
                <thead>
                  <tr className="border-b border-border text-muted-foreground">
                    <th className="px-4 py-3">#</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Mobile</th>
                    <th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Total Sales</th>
                    <th className="px-4 py-3 text-right">Last Visited</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCustomers?.length > 0 ? (
                    filteredCustomers.map((cust, index) => (
                      <tr
                        key={cust._id}
                        className="border-b border-border hover:bg-muted/50 micro-animation"
                      >
                        <td className="px-4 py-3 font-medium text-foreground">
                          {index + 1}
                        </td>
                        <td className="px-4 py-3">{cust.name}</td>
                        <td className="px-4 py-3">{cust.mobile}</td>
                        <td className="px-4 py-3">{cust.email || "-"}</td>
                        <td className="px-4 py-3">{cust?.totalSpent || 0}</td>
                        <td className="px-4 py-3 text-right space-x-2">
                          {cust.lastVisited
                            ? new Date(cust.lastVisited).toLocaleDateString()
                            : "Never"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan="6"
                        className="text-center text-muted-foreground py-4"
                      >
                        No customers found for selected filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default CustomerPage;
